/**
 * @module components/Digitizing.js
 * @name Digitizing
 * @copyright 2023 3Liz
 * @author BOISTEAULT Nicolas
 * @license MPL-2.0
 */

import { mainLizmap, mainEventDispatcher } from '../modules/Globals.js';
import { DigitizingAvailableTools } from '../modules/Digitizing.js'
import { html, render } from 'lit-html';

import '../images/svg/point.svg';
import '../images/svg/line.svg';
import '../images/svg/polygon.svg';
import '../images/svg/box.svg';
import '../images/svg/circle.svg';
import '../images/svg/freehand.svg';
import '../images/svg/text.svg';

import '../images/svg/pencil.svg';
import '../images/svg/edit.svg';
import '../images/svg/rotate.svg';
import '../images/svg/scaling.svg';
import '../images/svg/split.svg';
import '../images/svg/eraser.svg';
import '../images/svg/eraser-all.svg';
import '../images/svg/save.svg';

import '../images/svg/file-download.svg';
import '../images/svg/file-upload.svg';

/**
 * @summary Provides user interface for digitizing shapes and text
 * @augments HTMLElement
 *
 * @description By default, it provides a toolbar with draw tools, editing tools
 * (update, rotate, scale, split, delete, delete all), save, import and export tools.
 *
 * The context attribute is used to link the digitizing context to the Digitizing module context.
 * It is mandatory and provide a way to use this element for different contexts.
 *
 * The other attributes are:
 *  selected-tool - Start selected drawing tools one of DigitizingAvailableTools or available-tools
 *  available-tools - List of available drawing tools based on DigitizingAvailableTools
 *  save - Enable save capability
 *  import-export - Enable import / export capabilities
 *  measure - Enable measure capability
 *  text-tools - Enable text tools
 * @example <caption>Example of use</caption>
 * <lizmap-digitizing
 *     context="draw"
 *     selected-tool="box"
 *     available-tools="point,line,polygon,box,freehand"
 *     save
 *     import-export
 *     measure
 *     text-tools
 *     ></lizmap-digitizing>
 *
 * @listens Digitizing#digitizingDrawColor
 * @listens Digitizing#digitizingEditedFeatureRotation
 * @listens Digitizing#digitizingEditedFeatureScale
 * @listens Digitizing#digitizingEditedFeatureText
 * @listens Digitizing#digitizingEditionBegins
 * @listens Digitizing#digitizingEditionEnds
 * @listens Digitizing#digitizingErase
 * @listens Digitizing#digitizingErase.all
 * @listens Digitizing#digitizingErasingBegins
 * @listens Digitizing#digitizingErasingEnds
 * @listens Digitizing#digitizingFeatureDrawn
 * @listens Digitizing#digitizingMeasure
 * @listens Digitizing#digitizingRotate
 * @listens Digitizing#digitizingScaling
 * @listens Digitizing#digitizingSave
 * @listens Digitizing#digitizingSplit
 * @listens Digitizing#digitizingToolSelected
 * @listens Digitizing#digitizingVisibility
 */
export default class Digitizing extends HTMLElement {
    constructor() {
        super();
        this._toolSelected = DigitizingAvailableTools[0];
        this._availableTools = DigitizingAvailableTools.slice(1);
    }

    connectedCallback() {

        // Update available tools from attribute
        if (this.hasAttribute('available-tools')) {
            const attrAvailableTools = this.getAttribute('available-tools')
                .split(',')
                .map((item) => item.trim())
                .filter((item) => this._availableTools.includes(item));
            // update only if available tools will not be empty
            if (attrAvailableTools.length > 0) {
                this._availableTools = attrAvailableTools;
            }
        }
        // Update selected tool from attribute
        if (this.hasAttribute('selected-tool')) {
            const attrToolSelected = this.getAttribute('selected-tool');
            // update only if the selected tool is known
            if (this._availableTools.includes(attrToolSelected)) {
                this._toolSelected = attrToolSelected;
            }
        }
        // Set selected tool if only one tool is available
        if (this._availableTools.length === 1) {
            this._toolSelected = this._availableTools[0];
        }

        const svgToolIconTemplate = (tool) => {
            return html`
                <svg class="digitizing-tool-icon">
                    <use href="#${tool}"></use>
                </svg>
            `;
        }

        const toolButtonTemplate = (availableTools, toolSelected) => html`
            <div
                class="digitizing-buttons btn-group"
                data-original-title="${lizDict['digitizing.toolbar.drawTools']}"
                >
                <button href="#"
                    class="digitizing-selected-tool btn dropdown-toggle ${this.deactivate ? '' : 'active btn-primary'}"
                    value="${toolSelected}"
                    @click=${(event) => {this.toggleToolSelected(event)}}
                    data-toggle="dropdown"
                    >
                    <svg>
                        <use xlink:href="#pencil"></use>
                    </svg>
                    <!-- Display selected tool -->
                    ${availableTools
                        .filter(tool => toolSelected === tool)
                        .map(tool => html`
                            ${svgToolIconTemplate(tool)}
                        `)
                    }
                </button>
                ${availableTools.length != 1 ? html`
                <button class="btn dropdown-toggle" data-toggle="dropdown" href="#">
                    <span class="caret"></span>
                </button>
                <ul class="dropdown-menu">
                    ${availableTools
                        .map(tool => html`
                        <li
                            class="digitizing-${tool} btn ${toolSelected === tool ? 'active btn-primary' : ''}"
                            @click=${(event) => this.selectTool(event.currentTarget.dataset.value)}
                            data-value="${tool}"
                            data-bs-toggle="tooltip"
                            data-bs-title="${lizDict['digitizing.toolbar.'+tool]}"
                            >
                            ${svgToolIconTemplate(tool)}
                        </li>
                        `)
                    }
                </ul>` : ''}
            </div>
        `;

        const measureButtonTemplate = (hasMeasureVisible) => html`
            <button
                type="button"
                class="digitizing-toggle-measure btn ${hasMeasureVisible ? 'active btn-primary' : ''}"
                @click=${() => mainLizmap.digitizing.toggleMeasure()}
                data-original-title="${lizDict['digitizing.toolbar.measure']}"
                >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M17 3l4 4l-14 14l-4 -4z"></path>
                    <path d="M16 7l-1.5 -1.5"></path>
                    <path d="M13 10l-1.5 -1.5"></path>
                    <path d="M10 13l-1.5 -1.5"></path>
                    <path d="M7 16l-1.5 -1.5"></path>
                </svg>
            </button>
            `;

        const saveButtonTemplate = (isSaved) => html `
            <button
                type="button"
                class="digitizing-save btn ${isSaved ? 'active btn-primary' : ''}"
                @click=${()=> this.toggleSave()}
                data-original-title="${lizDict['digitizing.toolbar.save']}"
                >
                <svg>
                    <use xlink:href="#save" />
                </svg>
            </button>
            `;

        const textContentInputTemplate = () => html`
            <div class="digitizing-text-content form-row">
                <label for="textContent">
                    ${lizDict['digitizing.toolbar.textLabel']}
                </label>
                <textarea
                    id="textContent"
                    placeholder="${lizDict['digitizing.toolbar.newText']}"
                    .value=${mainLizmap.digitizing.editedFeatureText}
                    @input=${
                        event=> mainLizmap.digitizing.editedFeatureText = event.target.value
                    }
                    ></textarea>
            </div>
        `;

        const textRotationInputTemplate = () => html`
            <div class='digitizing-text-rotation form-row'>
                <label for="textRotation">
                    ${lizDict['digitizing.toolbar.textRotation']}
                </label>
                <div class="input-append">
                    <input
                        id="textRotation"
                        type="number"
                        .value=${mainLizmap.digitizing.editedFeatureTextRotation}
                        @input=${
                            event => mainLizmap.digitizing.editedFeatureTextRotation = parseInt(event.target.value)
                        }
                        >
                    <span class="add-on">°</span>
                </div>
            </div>
        `;

        const textScaleInputTemplate = () => html`
            <div class="digitizing-text-scale form-row">
                <label for="textScale">
                    ${lizDict['digitizing.toolbar.textScale']}
                </label>
                <input
                    id="textScale"
                    type="number"
                    min="1"
                    .value=${mainLizmap.digitizing.editedFeatureTextScale}
                    @input=${
                        event => mainLizmap.digitizing.editedFeatureTextScale = parseInt(event.target.value)
                    }
                    >
            </div>
            `;

        const textToolsTemplate = (hasEditedFeatures) => html`
            <form class="digitizing-text-tools ${hasEditedFeatures ? '' : 'hide'}">
                <details>
                    <summary>
                        ${lizDict['digitizing.toolbar.text']}
                    </summary>
                    ${lizDict['digitizing.toolbar.text.hint']}
                </details>
                ${textContentInputTemplate()}
                ${textRotationInputTemplate()}
                ${textScaleInputTemplate()}
            </form>
            `;

        const mainTemplate = (toolSelected) => html`
        <div class="digitizing">
            ${toolButtonTemplate(this._availableTools, toolSelected)}
            <input
                type="color"
                class="digitizing-color btn"
                .value="${mainLizmap.digitizing.drawColor}"
                @input=${(event) => mainLizmap.digitizing._userChangedColor(event.target.value)}
                data-original-title="${lizDict['digitizing.toolbar.color']}"
                >
            <button
                type="button"
                class="digitizing-edit btn ${mainLizmap.digitizing.isEdited ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleEdit()}
                data-original-title="${lizDict['digitizing.toolbar.edit']}"
                >
                <svg>
                    <use xlink:href="#edit"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-rotate btn ${mainLizmap.digitizing.isRotate ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleRotate()}
                data-original-title="${lizDict['digitizing.toolbar.rotate']}"
                >
                <svg>
                    <use xlink:href="#rotate"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-scaling btn ${mainLizmap.digitizing.isScaling ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleScaling()}
                data-bs-toggle="tooltip"
                data-bs-title="${lizDict['digitizing.toolbar.scaling']}"
                >
                <svg>
                    <use xlink:href="#scaling"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-split btn ${mainLizmap.digitizing.isSplitting ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleSplit()}
                data-original-title="${lizDict['digitizing.toolbar.split']}"
                >
                <svg>
                    <use xlink:href="#split"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-erase btn ${mainLizmap.digitizing.isErasing ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleErasing()}
                data-original-title="${lizDict['digitizing.toolbar.erase']}"
                >
                <svg>
                    <use xlink:href="#eraser"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-erase-all btn"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => this.eraseAll()}
                data-original-title="${lizDict['digitizing.toolbar.erase.all']}"
                >
                <svg>
                    <use xlink:href="#eraser-all"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-toggle-visibility btn"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleVisibility()}
                data-original-title="${lizDict['tree.button.checkbox']}"
                >
                <i class="icon-eye-${mainLizmap.digitizing.visibility ? 'open' : 'close'}"></i>
            </button>
            ${this.measureAvailable ? measureButtonTemplate(
                mainLizmap.digitizing.hasMeasureVisible,
            ) : ''}
            ${this.saveAvailable ? saveButtonTemplate(
                mainLizmap.digitizing.isSaved,
            ) : ''}
            <div class="digitizing-import-export ${this.importExportAvailable ? '' : 'hide'}">
                <div class="btn-group digitizing-export" data-original-title="${lizDict['attributeLayers.toolbar.btn.data.export.title']}">
                    <button
                        class="btn dropdown-toggle"
                        ?disabled=${!mainLizmap.digitizing.featureDrawn}
                        data-toggle="dropdown"
                        >
                        <svg>
                            <use xlink:href="#file-download"></use>
                        </svg>
                        <span class="caret"></span>
                    </button>
                    <ul class="dropdown-menu">
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('geojson')}>
                                GeoJSON
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('gpx')}>
                                GPX
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('kml')}>
                                KML
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('fgb')}>
                                FlatGeobuf
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="digitizing-import">
                    <label class="btn" data-original-title="${lizDict['digitizing.toolbar.import']}">
                        <svg>
                            <use xlink:href="#file-upload"></use>
                        </svg>
                        <input
                            class="hide"
                            type="file"
                            accept=".kml, .geojson, .json, .gpx, .zip, .fgb"
                            @change=${(event) => {
                                if (event.target.files.length > 0){
                                    const file = event.target.files[0];
                                    const parent = event.target.parentElement.parentElement;
                                    parent.querySelector('.file-name').textContent = file.name;
                                    mainLizmap.digitizing.import(file);
                                    event.target.value = '';
                                }
                            }}>
                    </label>
                    <span class="file-name"></span>
                </div>
            </div>
            <div class="digitizing-state hide">
                <div class="digitizing-save-state hide">${lizDict['digitizing.toolbar.save.state']}</div>
            </div>
            <div class="digitizing-constraints ${mainLizmap.digitizing.hasConstraintsPanelVisible ? '' : 'hide'}">
                <details>
                    <summary>
                        ${lizDict['digitizing.constraint.title']}
                    </summary>
                    ${lizDict['digitizing.constraint.details']}
                </details>
                <div class="digitizing-constraint-distance input-append">
                    <input
                        type="number"
                        placeholder="${lizDict['digitizing.constraint.distance']}"
                        class="distance"
                        min="0"
                        step="any"
                        @input=${
                            event => mainLizmap.digitizing.distanceConstraint = event.target.value
                        }
                        >
                    <span class="add-on">m</span>
                </div>
                <div class="digitizing-constraint-angle input-append">
                    <input
                        type="number"
                        placeholder="${lizDict['digitizing.constraint.angle']}"
                        class="angle"
                        step="any"
                        @input=${
                            event => mainLizmap.digitizing.angleConstraint = event.target.value
                        }
                        >
                    <span class="add-on">°</span>
                </div>
            </div>
            ${this.textToolsAvailable ? textToolsTemplate(
                mainLizmap.digitizing.editedFeatures.length != 0
            ) : ''}
        </div>`;

        render(
            mainTemplate(
                this.toolSelected,
            ),
            this,
        );

        // Add tooltip on buttons
        $('.digitizing-buttons, .digitizing .btn, .digitizing-export', this).tooltip({
            placement: 'top'
        });

        mainEventDispatcher.addListener(
            () => {
                if (!this.disabled) {
                    render(
                        mainTemplate(
                            this.toolSelected,
                        ),
                        this,
                    );
                }
            },
            [
                'digitizing.drawColor',
                'digitizing.editedFeatureRotation',
                'digitizing.editedFeatureScale',
                'digitizing.editedFeatureText',
                'digitizing.editionBegins',
                'digitizing.editionEnds',
                'digitizing.erase',
                'digitizing.erase.all',
                'digitizing.erasingBegins',
                'digitizing.erasingEnds',
                'digitizing.featureDrawn',
                'digitizing.measure',
                'digitizing.rotate',
                'digitizing.scaling',
                'digitizing.save',
                'digitizing.split',
                'digitizing.toolSelected',
                'digitizing.visibility',
            ]
        );
    }

    disconnectedCallback() {
    }

    /**
     * Digitizing context
     * The element attribute: context
     * @type {string}
     */
    get context() {
        if (this.hasAttribute('context')) {
            return this.getAttribute('context');
        }
        return 'draw';
    }

    /**
     * The element is deactivated
     * if the element is disabled
     * or if the tool is deactivated
     * @type {boolean}
     */
    get deactivate() {
        if (mainLizmap.digitizing.context !== this.context) {
            return true;
        }
        if (mainLizmap.digitizing.toolSelected === 'deactivate') {
            return true;
        }
        return false;
    }

    /**
     * The element is disabled if the context is not the same as the module
     * @type {boolean}
     */
    get disabled() {
        if (mainLizmap.digitizing.context !== this.context) {
            return true;
        }
        return false;
    }

    /**
     * Measure is available
     * The element has attribute: measure
     * @type {boolean}
     */
    get measureAvailable() {
        return this.hasAttribute('measure');
    }

    /**
     * Save is available
     * The element has attribute: save
     * @type {boolean}
     */
    get saveAvailable() {
        return this.hasAttribute('save');
    }

    /**
     * Import/export is available
     * The element has attribute: import-export
     * @type {boolean}
     */
    get importExportAvailable() {
        return this.hasAttribute('import-export');
    }

    /**
     * Text tools are available
     * The element has attribute: text-tools
     * @type {boolean}
     */
    get textToolsAvailable() {
        return this.hasAttribute('text-tools');
    }

    /**
     * The available tools
     * The element attribute: available-tools
     * All or part of DigitizingAvailableTools except deactivate
     * @see DigitizingAvailableTools
     * @type {string}
     */
    get availableTools() {
        return this._availableTools;
    }

    /**
     * The selected tool
     * The element attribute: selected-tool
     * @type {string}
     */
    get toolSelected() {
        return this._toolSelected;
    }

    /**
     * Setting the selected
     * @see DigitizingAvailableTools
     * @param {string} tool - switch new OL map on top of OL2 one
     */
    set toolSelected(tool) {
        if (this._availableTools.includes(tool)) {
            this._toolSelected = tool;
            mainLizmap.digitizing.toolSelected = tool;
        }
    }

    /**
     * Select tool
     * @param {string} tool - The tool to select
     * @returns {boolean} - True if the tool is selected, false otherwise
     */
    selectTool(tool) {
        this.toolSelected = tool;
        return this.toolSelected === tool;
    }

    /**
     * Toggle selected tool
     * @param {MouseEvent} event - The click event on the button
     */
    toggleToolSelected(event) {
        const firstAvailableTools = DigitizingAvailableTools[0];
        if (this.toolSelected === firstAvailableTools) {
            return;
        } else if (mainLizmap.digitizing.toolSelected !== firstAvailableTools) {
            mainLizmap.digitizing.toolSelected = firstAvailableTools;
        } else {
            mainLizmap.digitizing.toolSelected = this.toolSelected;
        }
        event.stopPropagation();
    }

    /**
     * Erase all features
     * @returns {boolean} - False if the user cancels the action
     */
    eraseAll() {
        if (!confirm(lizDict['digitizing.confirm.erase.all'])) {
            return false;
        }
        mainLizmap.digitizing.eraseAll();
        return true;
    }

    /*
     * Toggle save state
     */
    toggleSave() {
        mainLizmap.digitizing.toggleSave();
        if (mainLizmap.digitizing.isSaved) {
            this.querySelector('button.digitizing-save').dataset.originalTitle =
                lizDict['digitizing.toolbar.save.remove'];
            this.querySelector('div.digitizing-save-state').classList.remove('hide');
            this.querySelector('div.digitizing-state').classList.remove('hide');
        } else {
            this.querySelector('button.digitizing-save').dataset.originalTitle =
                lizDict['digitizing.toolbar.save'];
            this.querySelector('div.digitizing-save-state').classList.add('hide');
            this.querySelector('div.digitizing-state').classList.add('hide');
        }
    }
}
/**
 * @module components/Digitizing.js
 * @name Digitizing
 * @copyright 2023 3Liz
 * @author BOISTEAULT Nicolas
 * @license MPL-2.0
 */

import { mainLizmap, mainEventDispatcher } from '../modules/Globals.js';
import { DigitizingAvailableTools } from '../modules/Digitizing.js'
import { html, render } from 'lit-html';

import '../images/svg/point.svg';
import '../images/svg/line.svg';
import '../images/svg/polygon.svg';
import '../images/svg/box.svg';
import '../images/svg/circle.svg';
import '../images/svg/freehand.svg';
import '../images/svg/text.svg';

import '../images/svg/pencil.svg';
import '../images/svg/edit.svg';
import '../images/svg/rotate.svg';
import '../images/svg/scaling.svg';
import '../images/svg/split.svg';
import '../images/svg/eraser.svg';
import '../images/svg/eraser-all.svg';
import '../images/svg/save.svg';

import '../images/svg/file-download.svg';
import '../images/svg/file-upload.svg';

/**
 * @summary Provides user interface for digitizing shapes and text
 * @augments HTMLElement
 *
 * @description By default, it provides a toolbar with draw tools, editing tools
 * (update, rotate, scale, split, delete, delete all), save, import and export tools.
 *
 * The context attribute is used to link the digitizing context to the Digitizing module context.
 * It is mandatory and provide a way to use this element for different contexts.
 *
 * The other attributes are:
 *  selected-tool - Start selected drawing tools one of DigitizingAvailableTools or available-tools
 *  available-tools - List of available drawing tools based on DigitizingAvailableTools
 *  save - Enable save capability
 *  import-export - Enable import / export capabilities
 *  measure - Enable measure capability
 *  text-tools - Enable text tools
 * @example <caption>Example of use</caption>
 * <lizmap-digitizing
 *     context="draw"
 *     selected-tool="box"
 *     available-tools="point,line,polygon,box,freehand"
 *     save
 *     import-export
 *     measure
 *     text-tools
 *     ></lizmap-digitizing>
 *
 * @listens Digitizing#digitizingDrawColor
 * @listens Digitizing#digitizingEditedFeatureRotation
 * @listens Digitizing#digitizingEditedFeatureScale
 * @listens Digitizing#digitizingEditedFeatureText
 * @listens Digitizing#digitizingEditionBegins
 * @listens Digitizing#digitizingEditionEnds
 * @listens Digitizing#digitizingErase
 * @listens Digitizing#digitizingErase.all
 * @listens Digitizing#digitizingErasingBegins
 * @listens Digitizing#digitizingErasingEnds
 * @listens Digitizing#digitizingFeatureDrawn
 * @listens Digitizing#digitizingMeasure
 * @listens Digitizing#digitizingRotate
 * @listens Digitizing#digitizingScaling
 * @listens Digitizing#digitizingSave
 * @listens Digitizing#digitizingSplit
 * @listens Digitizing#digitizingToolSelected
 * @listens Digitizing#digitizingVisibility
 */
export default class Digitizing extends HTMLElement {
    constructor() {
        super();
        this._toolSelected = DigitizingAvailableTools[0];
        this._availableTools = DigitizingAvailableTools.slice(1);
    }

    connectedCallback() {

        // Update available tools from attribute
        if (this.hasAttribute('available-tools')) {
            const attrAvailableTools = this.getAttribute('available-tools')
                .split(',')
                .map((item) => item.trim())
                .filter((item) => this._availableTools.includes(item));
            // update only if available tools will not be empty
            if (attrAvailableTools.length > 0) {
                this._availableTools = attrAvailableTools;
            }
        }
        // Update selected tool from attribute
        if (this.hasAttribute('selected-tool')) {
            const attrToolSelected = this.getAttribute('selected-tool');
            // update only if the selected tool is known
            if (this._availableTools.includes(attrToolSelected)) {
                this._toolSelected = attrToolSelected;
            }
        }
        // Set selected tool if only one tool is available
        if (this._availableTools.length === 1) {
            this._toolSelected = this._availableTools[0];
        }

        const svgToolIconTemplate = (tool) => {
            return html`
                <svg class="digitizing-tool-icon">
                    <use href="#${tool}"></use>
                </svg>
            `;
        }

        const toolButtonTemplate = (availableTools, toolSelected) => html`
            <div
                class="digitizing-buttons btn-group"
                data-original-title="${lizDict['digitizing.toolbar.drawTools']}"
                >
                <button href="#"
                    class="digitizing-selected-tool btn dropdown-toggle ${this.deactivate ? '' : 'active btn-primary'}"
                    value="${toolSelected}"
                    @click=${(event) => {this.toggleToolSelected(event)}}
                    data-toggle="dropdown"
                    >
                    <svg>
                        <use xlink:href="#pencil"></use>
                    </svg>
                    <!-- Display selected tool -->
                    ${availableTools
                        .filter(tool => toolSelected === tool)
                        .map(tool => html`
                            ${svgToolIconTemplate(tool)}
                        `)
                    }
                </button>
                ${availableTools.length != 1 ? html`
                <button class="btn dropdown-toggle" data-toggle="dropdown" href="#">
                    <span class="caret"></span>
                </button>
                <ul class="dropdown-menu">
                    ${availableTools
                        .map(tool => html`
                        <li
                            class="digitizing-${tool} btn ${toolSelected === tool ? 'active btn-primary' : ''}"
                            @click=${(event) => this.selectTool(event.currentTarget.dataset.value)}
                            data-value="${tool}"
                            data-bs-toggle="tooltip"
                            data-bs-title="${lizDict['digitizing.toolbar.'+tool]}"
                            >
                            ${svgToolIconTemplate(tool)}
                        </li>
                        `)
                    }
                </ul>` : ''}
            </div>
        `;

        const measureButtonTemplate = (hasMeasureVisible) => html`
            <button
                type="button"
                class="digitizing-toggle-measure btn ${hasMeasureVisible ? 'active btn-primary' : ''}"
                @click=${() => mainLizmap.digitizing.toggleMeasure()}
                data-original-title="${lizDict['digitizing.toolbar.measure']}"
                >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M17 3l4 4l-14 14l-4 -4z"></path>
                    <path d="M16 7l-1.5 -1.5"></path>
                    <path d="M13 10l-1.5 -1.5"></path>
                    <path d="M10 13l-1.5 -1.5"></path>
                    <path d="M7 16l-1.5 -1.5"></path>
                </svg>
            </button>
            `;

        const saveButtonTemplate = (isSaved) => html `
            <button
                type="button"
                class="digitizing-save btn ${isSaved ? 'active btn-primary' : ''}"
                @click=${()=> this.toggleSave()}
                data-original-title="${lizDict['digitizing.toolbar.save']}"
                >
                <svg>
                    <use xlink:href="#save" />
                </svg>
            </button>
            `;

        const textContentInputTemplate = () => html`
            <div class="digitizing-text-content form-row">
                <label for="textContent">
                    ${lizDict['digitizing.toolbar.textLabel']}
                </label>
                <textarea
                    id="textContent"
                    placeholder="${lizDict['digitizing.toolbar.newText']}"
                    .value=${mainLizmap.digitizing.editedFeatureText}
                    @input=${
                        event=> mainLizmap.digitizing.editedFeatureText = event.target.value
                    }
                    ></textarea>
            </div>
        `;

        const textRotationInputTemplate = () => html`
            <div class='digitizing-text-rotation form-row'>
                <label for="textRotation">
                    ${lizDict['digitizing.toolbar.textRotation']}
                </label>
                <div class="input-append">
                    <input
                        id="textRotation"
                        type="number"
                        .value=${mainLizmap.digitizing.editedFeatureTextRotation}
                        @input=${
                            event => mainLizmap.digitizing.editedFeatureTextRotation = parseInt(event.target.value)
                        }
                        >
                    <span class="add-on">°</span>
                </div>
            </div>
        `;

        const textScaleInputTemplate = () => html`
            <div class="digitizing-text-scale form-row">
                <label for="textScale">
                    ${lizDict['digitizing.toolbar.textScale']}
                </label>
                <input
                    id="textScale"
                    type="number"
                    min="1"
                    .value=${mainLizmap.digitizing.editedFeatureTextScale}
                    @input=${
                        event => mainLizmap.digitizing.editedFeatureTextScale = parseInt(event.target.value)
                    }
                    >
            </div>
            `;

        const textToolsTemplate = (hasEditedFeatures) => html`
            <form class="digitizing-text-tools ${hasEditedFeatures ? '' : 'hide'}">
                <details>
                    <summary>
                        ${lizDict['digitizing.toolbar.text']}
                    </summary>
                    ${lizDict['digitizing.toolbar.text.hint']}
                </details>
                ${textContentInputTemplate()}
                ${textRotationInputTemplate()}
                ${textScaleInputTemplate()}
            </form>
            `;

        const mainTemplate = (toolSelected) => html`
        <div class="digitizing">
            ${toolButtonTemplate(this._availableTools, toolSelected)}
            <input
                type="color"
                class="digitizing-color btn"
                .value="${mainLizmap.digitizing.drawColor}"
                @input=${(event) => mainLizmap.digitizing._userChangedColor(event.target.value)}
                data-original-title="${lizDict['digitizing.toolbar.color']}"
                >
            <button
                type="button"
                class="digitizing-edit btn ${mainLizmap.digitizing.isEdited ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleEdit()}
                data-original-title="${lizDict['digitizing.toolbar.edit']}"
                >
                <svg>
                    <use xlink:href="#edit"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-rotate btn ${mainLizmap.digitizing.isRotate ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleRotate()}
                data-original-title="${lizDict['digitizing.toolbar.rotate']}"
                >
                <svg>
                    <use xlink:href="#rotate"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-scaling btn ${mainLizmap.digitizing.isScaling ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleScaling()}
                data-bs-toggle="tooltip"
                data-bs-title="${lizDict['digitizing.toolbar.scaling']}"
                >
                <svg>
                    <use xlink:href="#scaling"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-split btn ${mainLizmap.digitizing.isSplitting ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleSplit()}
                data-original-title="${lizDict['digitizing.toolbar.split']}"
                >
                <svg>
                    <use xlink:href="#split"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-erase btn ${mainLizmap.digitizing.isErasing ? 'active btn-primary' : ''}"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleErasing()}
                data-original-title="${lizDict['digitizing.toolbar.erase']}"
                >
                <svg>
                    <use xlink:href="#eraser"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-erase-all btn"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => this.eraseAll()}
                data-original-title="${lizDict['digitizing.toolbar.erase.all']}"
                >
                <svg>
                    <use xlink:href="#eraser-all"/>
                </svg>
            </button>
            <button
                type="button"
                class="digitizing-toggle-visibility btn"
                ?disabled=${!mainLizmap.digitizing.featureDrawn}
                @click=${() => mainLizmap.digitizing.toggleVisibility()}
                data-original-title="${lizDict['tree.button.checkbox']}"
                >
                <i class="icon-eye-${mainLizmap.digitizing.visibility ? 'open' : 'close'}"></i>
            </button>
            ${this.measureAvailable ? measureButtonTemplate(
                mainLizmap.digitizing.hasMeasureVisible,
            ) : ''}
            ${this.saveAvailable ? saveButtonTemplate(
                mainLizmap.digitizing.isSaved,
            ) : ''}
            <div class="digitizing-import-export ${this.importExportAvailable ? '' : 'hide'}">
                <div class="btn-group digitizing-export" data-original-title="${lizDict['attributeLayers.toolbar.btn.data.export.title']}">
                    <button
                        class="btn dropdown-toggle"
                        ?disabled=${!mainLizmap.digitizing.featureDrawn}
                        data-toggle="dropdown"
                        >
                        <svg>
                            <use xlink:href="#file-download"></use>
                        </svg>
                        <span class="caret"></span>
                    </button>
                    <ul class="dropdown-menu">
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('geojson')}>
                                GeoJSON
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('gpx')}>
                                GPX
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('kml')}>
                                KML
                            </a>
                        </li>
                        <li>
                            <a href="#" @click=${() => mainLizmap.digitizing.download('fgb')}>
                                FlatGeobuf
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="digitizing-import">
                    <label class="btn" data-original-title="${lizDict['digitizing.toolbar.import']}">
                        <svg>
                            <use xlink:href="#file-upload"></use>
                        </svg>
                        <input
                            class="hide"
                            type="file"
                            accept=".kml, .geojson, .json, .gpx, .zip, .fgb"
                            @change=${(event) => {
                                if (event.target.files.length > 0){
                                    const file = event.target.files[0];
                                    const parent = event.target.parentElement.parentElement;
                                    parent.querySelector('.file-name').textContent = file.name;
                                    mainLizmap.digitizing.import(file);
                                    event.target.value = '';
                                }
                            }}>
                    </label>
                    <span class="file-name"></span>
                </div>
            </div>
            <div class="digitizing-state hide">
                <div class="digitizing-save-state hide">${lizDict['digitizing.toolbar.save.state']}</div>
            </div>
            <div class="digitizing-constraints ${mainLizmap.digitizing.hasConstraintsPanelVisible ? '' : 'hide'}">
                <details>
                    <summary>
                        ${lizDict['digitizing.constraint.title']}
                    </summary>
                    ${lizDict['digitizing.constraint.details']}
                </details>
                <div class="digitizing-constraint-distance input-append">
                    <input
                        type="number"
                        placeholder="${lizDict['digitizing.constraint.distance']}"
                        class="distance"
                        min="0"
                        step="any"
                        @input=${
                            event => mainLizmap.digitizing.distanceConstraint = event.target.value
                        }
                        >
                    <span class="add-on">m</span>
                </div>
                <div class="digitizing-constraint-angle input-append">
                    <input
                        type="number"
                        placeholder="${lizDict['digitizing.constraint.angle']}"
                        class="angle"
                        step="any"
                        @input=${
                            event => mainLizmap.digitizing.angleConstraint = event.target.value
                        }
                        >
                    <span class="add-on">°</span>
                </div>
            </div>
            ${this.textToolsAvailable ? textToolsTemplate(
                mainLizmap.digitizing.editedFeatures.length != 0
            ) : ''}
        </div>`;

        render(
            mainTemplate(
                this.toolSelected,
            ),
            this,
        );

        // Add tooltip on buttons
        $('.digitizing-buttons, .digitizing .btn, .digitizing-export', this).tooltip({
            placement: 'top'
        });

        mainEventDispatcher.addListener(
            () => {
                if (!this.disabled) {
                    render(
                        mainTemplate(
                            this.toolSelected,
                        ),
                        this,
                    );
                }
            },
            [
                'digitizing.drawColor',
                'digitizing.editedFeatureRotation',
                'digitizing.editedFeatureScale',
                'digitizing.editedFeatureText',
                'digitizing.editionBegins',
                'digitizing.editionEnds',
                'digitizing.erase',
                'digitizing.erase.all',
                'digitizing.erasingBegins',
                'digitizing.erasingEnds',
                'digitizing.featureDrawn',
                'digitizing.measure',
                'digitizing.rotate',
                'digitizing.scaling',
                'digitizing.save',
                'digitizing.split',
                'digitizing.toolSelected',
                'digitizing.visibility',
            ]
        );
    }

    disconnectedCallback() {
    }

    /**
     * Digitizing context
     * The element attribute: context
     * @type {string}
     */
    get context() {
        if (this.hasAttribute('context')) {
            return this.getAttribute('context');
        }
        return 'draw';
    }

    /**
     * The element is deactivated
     * if the element is disabled
     * or if the tool is deactivated
     * @type {boolean}
     */
    get deactivate() {
        if (mainLizmap.digitizing.context !== this.context) {
            return true;
        }
        if (mainLizmap.digitizing.toolSelected === 'deactivate') {
            return true;
        }
        return false;
    }

    /**
     * The element is disabled if the context is not the same as the module
     * @type {boolean}
     */
    get disabled() {
        if (mainLizmap.digitizing.context !== this.context) {
            return true;
        }
        return false;
    }

    /**
     * Measure is available
     * The element has attribute: measure
     * @type {boolean}
     */
    get measureAvailable() {
        return this.hasAttribute('measure');
    }

    /**
     * Save is available
     * The element has attribute: save
     * @type {boolean}
     */
    get saveAvailable() {
        return this.hasAttribute('save');
    }

    /**
     * Import/export is available
     * The element has attribute: import-export
     * @type {boolean}
     */
    get importExportAvailable() {
        return this.hasAttribute('import-export');
    }

    /**
     * Text tools are available
     * The element has attribute: text-tools
     * @type {boolean}
     */
    get textToolsAvailable() {
        return this.hasAttribute('text-tools');
    }

    /**
     * The available tools
     * The element attribute: available-tools
     * All or part of DigitizingAvailableTools except deactivate
     * @see DigitizingAvailableTools
     * @type {string}
     */
    get availableTools() {
        return this._availableTools;
    }

    /**
     * The selected tool
     * The element attribute: selected-tool
     * @type {string}
     */
    get toolSelected() {
        return this._toolSelected;
    }

    /**
     * Setting the selected
     * @see DigitizingAvailableTools
     * @param {string} tool - switch new OL map on top of OL2 one
     */
    set toolSelected(tool) {
        if (this._availableTools.includes(tool)) {
            this._toolSelected = tool;
            mainLizmap.digitizing.toolSelected = tool;
        }
    }

    /**
     * Select tool
     * @param {string} tool - The tool to select
     * @returns {boolean} - True if the tool is selected, false otherwise
     */
    selectTool(tool) {
        this.toolSelected = tool;
        return this.toolSelected === tool;
    }

    /**
     * Toggle selected tool
     * @param {MouseEvent} event - The click event on the button
     */
    toggleToolSelected(event) {
        const firstAvailableTools = DigitizingAvailableTools[0];
        if (this.toolSelected === firstAvailableTools) {
            return;
        } else if (mainLizmap.digitizing.toolSelected !== firstAvailableTools) {
            mainLizmap.digitizing.toolSelected = firstAvailableTools;
        } else {
            mainLizmap.digitizing.toolSelected = this.toolSelected;
        }
        event.stopPropagation();
    }

    /**
     * Erase all features
     * @returns {boolean} - False if the user cancels the action
     */
    eraseAll() {
        if (!confirm(lizDict['digitizing.confirm.erase.all'])) {
            return false;
        }
        mainLizmap.digitizing.eraseAll();
        return true;
    }

    /*
     * Toggle save state
     */
    toggleSave() {
        mainLizmap.digitizing.toggleSave();
        if (mainLizmap.digitizing.isSaved) {
            this.querySelector('button.digitizing-save').dataset.originalTitle =
                lizDict['digitizing.toolbar.save.remove'];
            this.querySelector('div.digitizing-save-state').classList.remove('hide');
            this.querySelector('div.digitizing-state').classList.remove('hide');
        } else {
            this.querySelector('button.digitizing-save').dataset.originalTitle =
                lizDict['digitizing.toolbar.save'];
            this.querySelector('div.digitizing-save-state').classList.add('hide');
            this.querySelector('div.digitizing-state').classList.add('hide');
        }
    }
}
